import type React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Modal,
  TextField,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import axios from "axios";
import DynamicTable from "../../../../components/lab-dynamic-table";
import { useNavigate } from "react-router-dom";
import { useBloodBankDateQuery } from "../../blood-bank-date";
import { useSelector } from "react-redux";
import { generateCrossMatchPDF } from "./generateCrossMatchPDF";
import jsPDF from "jspdf";
import jsbarcode from "jsbarcode";
// "D:\new clone pk-hims\hims-frontend\public\thermal-printer-icon.png"
import thermalPrinterIcon from "../../../../thermal-printer-icon.png";
import SearchIcon from "@mui/icons-material/Search";
import DateRangeSelector from "../../../../components/DateRangeSelector";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const CrossMatchReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [crossMatch, setAllCrossMatch] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const {
    dateRange,
    setDateRange,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
  } = useBloodBankDateQuery(setPage);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const profile = useSelector((state: any) => state?.profileSlice?.profile);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  // const [pdfContent, setPdfContent] = useState<string | null>(null);
  const [pdfContent, setPdfContent] = useState<string | undefined>(undefined);
  const [currentPrintData, setCurrentPrintData] = useState<any>(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [resultFilter, setResultFilter] = useState("");
  const [bloodGroup, setBloodGroup] = useState("");

  const projectProfile = useSelector(
    (state: any) => state.projectProfileSlice.projectProfile
  );

  const hospitalName = projectProfile?.hospitalName || "N/A";
  const firstWord = hospitalName.split(" ")[0];

  // Restore scroll position after page reload
  useEffect(() => {
    const savedState = sessionStorage.getItem("crossMatchReportState");
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        if (parsedState.scrollPosition) {
          window.scrollTo(0, parsedState.scrollPosition);
        }
        sessionStorage.removeItem("crossMatchReportState");
      } catch (error) {
        console.error("Error restoring report state:", error);
      }
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch blood bags data from API
  useEffect(() => {
    fetchBloodBags();
  }, [rowsPerPage, page, fromDate, toDate, search, resultFilter, bloodGroup]);

  const fetchBloodBags = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/cross-match/findAllCrossMatches`,
        {
          params: {
            pageNo: page + 1,
            count: rowsPerPage,
            ...(fromDate ? { fromDate } : {}),
            ...(toDate ? { toDate } : {}),
            ...(search ? { search } : {}),
            ...(resultFilter ? { crossMatchResult: resultFilter } : {}),
            ...(bloodGroup ? { bloodGroup } : {}),
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data?.data) {
        setAllCrossMatch(response.data.data);
        setTotal(response.data.totalCount || 0);
      } else {
        setAllCrossMatch([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching cross matches:", error);
      toast.error("Failed to fetch cross matches");
      setAllCrossMatch([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkForNewCrossMatch = async () => {
      const newlyAddedCrossMatch = localStorage.getItem("newlyAddedCrossMatch");
      if (newlyAddedCrossMatch && !loading) {
        try {
          const parsedData = JSON.parse(newlyAddedCrossMatch);
          console.log("Found newly added cross match:", parsedData);

          const matchIndex = crossMatch.findIndex(
            item =>
              item.bagNo === parsedData.bagNo &&
              item.slipNo === parsedData.slipNo
          );

          if (matchIndex === -1) {
            console.log(
              "Cross match not found in loaded data, using stored data for printing"
            );
            const printNewCrossMatch = () => {
              const performerName =
                parsedData.performedBy ||
                profile?.data?.fullName ||
                profile?.data?.firstName ||
                "";
              const doc = generateCrossMatchPDF(
                parsedData,
                formatDate,
                performerName,
                hospitalName
              );

              if (!doc) {
                toast.error("Failed to generate PDF");
                return;
              }

              const pdfBlob = doc.output("blob");
              const blobUrl = URL.createObjectURL(pdfBlob);

              const iframe = document.createElement("iframe");
              iframe.style.display = "none";
              document.body.appendChild(iframe);

              iframe.onload = () => {
                try {
                  setTimeout(() => {
                    if (iframe.contentWindow) {
                      iframe.contentWindow.focus();
                      iframe.contentWindow.print();

                      iframe.contentWindow.onafterprint = () => {
                        document.body.removeChild(iframe);
                        URL.revokeObjectURL(blobUrl);
                      };
                    }
                  }, 1000);
                } catch (e) {
                  console.error("Error triggering print:", e);
                  window.open(blobUrl, "_blank");
                  URL.revokeObjectURL(blobUrl);
                }
              };

              iframe.src = blobUrl;

              const backupCleanupTimer = setTimeout(() => {
                try {
                  if (document.body.contains(iframe)) {
                    document.body.removeChild(iframe);
                    URL.revokeObjectURL(blobUrl);
                  }
                } catch (e) {
                  console.error("Error during backup cleanup:", e);
                }
              }, 100000);
            };
            setTimeout(printNewCrossMatch, 1000);
          } else {
            console.log(
              "Found cross match in loaded data at index:",
              matchIndex
            );
            setTimeout(() => {
              handlePrint(matchIndex);
              navigate("/blood-bank/cross-match-report");
            }, 1000);
          }

          localStorage.removeItem("newlyAddedCrossMatch");
        } catch (error) {
          console.error("Error processing newly added cross match:", error);
          localStorage.removeItem("newlyAddedCrossMatch");
        }
      }
    };
    if (!loading) {
      checkForNewCrossMatch();
    }
  }, [crossMatch, loading]);

  const handleEdit = (rowIndex: number) => {
    const crossm = crossMatch[rowIndex];
    console.log("Editing cross match:", crossm);

    if (!crossm.bagNo || !crossm.slipNo) {
      toast.error("Missing required data for editing");
      return;
    }

    localStorage.setItem("selectetmatches", JSON.stringify(crossm));
    navigate("/blood-crossmatch");
  };

  const handlePrint = (rowIndex: number) => {
    const crossm = crossMatch[rowIndex];
    if (!crossm) {
      console.error("No cross match data available");
      return;
    }

    const performerName =
      crossm.performedBy ||
      profile?.data?.fullName ||
      profile?.data?.firstName ||
      "";

    const doc = generateCrossMatchPDF(
      crossm,
      formatDate,
      performerName,
      hospitalName
    );

    if (!doc) {
      toast.error("Failed to generate PDF");
      return;
    }

    const pdfBlob = doc.output("blob");
    const blobUrl = URL.createObjectURL(pdfBlob);

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    iframe.onload = () => {
      try {
        setTimeout(() => {
          if (iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();

            iframe.contentWindow.onafterprint = () => {
              document.body.removeChild(iframe);
              URL.revokeObjectURL(blobUrl);
            };
          }
        }, 1000);
      } catch (e) {
        console.error("Error triggering print:", e);
        window.open(blobUrl, "_blank");
        URL.revokeObjectURL(blobUrl);
      }
    };

    iframe.src = blobUrl;

    const backupCleanupTimer = setTimeout(() => {
      try {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(blobUrl);
        }
      } catch (e) {
        console.error("Error during backup cleanup:", e);
      }
    }, 300000);
  };
  const calculateAgeFromDOB = (dob?: string): string => {
    if (!dob) return "N/A";

    const birthDate = dayjs(
      dob,
      ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY"],
      true
    );
    if (!birthDate.isValid()) return "N/A";

    const today = dayjs();

    const years = today.diff(birthDate, "year");
    const months = today.diff(birthDate.add(years, "year"), "month");
    const days = today.diff(
      birthDate.add(years, "year").add(months, "month"),
      "day"
    );

    return `${years}Y-${months}M-${days}D`;
  };

  const handlePrintDesign = (rowIndex: number) => {
    const crossm = crossMatch[rowIndex];
    if (!crossm) {
      console.error("No cross match data available");
      toast.error("No cross match data available");
      return;
    }

    try {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [80, 120],
      });

      const margin = 5;
      const pageWidth = 80;
      let yPos = 5;

      // Use pageHeaderData for the header
      const pageHeader = crossm.pageHeaderData || {};

      //const hospitalTitle = pageHeader.title || projectProfile?.hospitalName;
      const hospitalTitle =
        pageHeader.title && pageHeader.title.trim() !== ""
          ? pageHeader.title
          : projectProfile?.hospitalName || "N/A";
      const firstWord = hospitalTitle.split(" ")[0];
      // const address = pageHeader.address || projectProfile?.city;
      const address =
        pageHeader.address && pageHeader.address.trim() !== ""
          ? pageHeader.address
          : projectProfile?.address || "";

      const logoUrl = pageHeader.logo || null;

      // Add logo if available
      // if (logoUrl) {
      //   try {
      //     doc.addImage(logoUrl, "PNG", margin, yPos, 20, 10); // Adjust size and position as needed
      //     yPos += 12;
      //   } catch (error) {
      //     console.error("Error adding logo to PDF:", error);
      //     // Fallback to text-only header if logo fails
      //   }
      // }

      // Add hospital title and address
      doc.setFont("times", "bold");
      doc.setFontSize(14);
      doc.text(hospitalTitle, pageWidth / 2, yPos, { align: "center" });
      yPos += 5;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(address, pageWidth / 2, yPos, { align: "center" });

      yPos += 2;
      doc.setLineWidth(0.5);
      doc.line(margin, yPos, pageWidth - margin, yPos);

      yPos += 2;
      const slipNo = crossm.slipNo || "N/A";
      const slipNoCanvas = document.createElement("canvas");
      slipNoCanvas.width = 800;
      slipNoCanvas.height = 100;
      const ctx = slipNoCanvas.getContext("2d");
      if (ctx) {
        ctx.imageSmoothingEnabled = false;
      }
      jsbarcode(slipNoCanvas, slipNo, {
        format: "CODE128",
        width: 2.0,
        height: 17,
        displayValue: false,
        margin: 0,
      });
      const slipNoDataUrl = slipNoCanvas.toDataURL("image/png", 1.0);
      const barcodeWidth1 = 20;
      const barcodeX1 = margin;
      doc.addImage(slipNoDataUrl, "PNG", barcodeX1, yPos, barcodeWidth1, 10);
      yPos += 15;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Slip No: ${slipNo}`, margin, yPos);
      doc.text(
        `MR No: ${crossm.patientDetails?.mrn || "N/A"}`,
        pageWidth - margin,
        yPos,
        { align: "right" }
      );
      yPos += 5;

       doc.text(
        `Ref MRN: ${crossm.patientDetails?.ref_mrn || "-"}`,
        margin,
        yPos
      );
      yPos += 5;

      doc.text(
        `P. Name: ${crossm.patientDetails?.patientName || "N/A"}`,
        margin,
        yPos
      );
      yPos += 5;
      doc.text(
        `Guardian Name: ${crossm.patientDetails?.guardiansName || "N/A"}`,
        margin,
        yPos
      );
      yPos += 5;
      doc.text(
        `Gender: ${crossm.patientDetails?.gender || "N/A"}`,
        margin,
        yPos
      );
      yPos += 5;
      console.log("DOB in handlePrintDesign:", crossm.patientDetails?.dob);
      doc.text(
        `Age: ${calculateAgeFromDOB(crossm.patientDetails?.dob)} `,
        margin,
        yPos
      );
      yPos += 5;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      const bloodProduct =
        crossm.bagComponent?.component ||
        (Array.isArray(crossm.bagData?.bloodCell)
          ? crossm.bagData.bloodCell.join(", ")
          : crossm.bagData?.bloodCell) ||
        "N/A";
      const maxWidth = 50;
      const bloodProductText = `Product: ${bloodProduct}`;
      const truncatedText = doc.text(bloodProductText, margin, yPos);

      yPos += 6;
      yPos += 5;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(32);
      doc.text(
        `${crossm.slipData?.bloodGroup || "N/A"}`,
        pageWidth - 10,
        yPos - 10,
        { align: "center" }
      );
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(
        `${crossm.slipData?.rhFactor === "+" ? "POSITIVE" : "NEGATIVE"}`,
        pageWidth - 10,
        yPos - 5,
        { align: "center" }
      );

      doc.setLineWidth(0.5);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 5;

      doc.setFontSize(10);
      doc.text(`Bag No: ${crossm.bagNo || "N/A"}`, margin, yPos);
      yPos += 5;
      doc.text(
        `Collection Date: ${formatDate(crossm.bagData?.collectionDate) || "N/A"
        }`,
        margin,
        yPos
      );
      yPos += 5;
      doc.text(
        `Expire Date: ${formatDate(crossm?.bagComponent?.expiryDate) || "N/A"}`,
        margin,
        yPos
      );
      yPos += 5;

      doc.setFontSize(10);
      const bloodProductText1 = `Product: ${bloodProduct}`;

      doc.text(bloodProductText1, margin, yPos);
      yPos += 1;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(32);
      doc.text(
        `${crossm.bagData?.bloodGroup || "N/A"}`,
        pageWidth - 10,
        yPos - 10,
        { align: "center" }
      );
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(
        `${crossm.bagData?.rhFactor === "+" ? "POSITIVE" : "NEGATIVE"}`,
        pageWidth - 10,
        yPos - 5,
        { align: "center" }
      );

      yPos += 1;
      const bagNo = crossm.bagNo || "N/A";
      const bagNoCanvas = document.createElement("canvas");
      bagNoCanvas.width = 800;
      bagNoCanvas.height = 100;
      const ctx1 = bagNoCanvas.getContext("2d");
      if (ctx1) {
        ctx1.imageSmoothingEnabled = false;
      }
      jsbarcode(bagNoCanvas, bagNo, {
        format: "CODE128",
        width: 2.0,
        height: 17,
        displayValue: false,
        margin: 0,
      });
      const bagNoDataUrl = bagNoCanvas.toDataURL("image/png", 1.0);
      const barcodeWidth = 20;
      const barcodeX = margin;
      doc.addImage(bagNoDataUrl, "PNG", barcodeX, yPos, barcodeWidth, 10);
      yPos += 15;

      doc.setFontSize(6);
      const longText =
        `This blood product has been screened for HBsAg, HCV, HIV, MP & VDRL. ${crossm.bagComponent.storageMessage}`;
      const wrappedText = doc.splitTextToSize(longText, pageWidth - 2 * margin);
      doc.text(wrappedText, margin, yPos);
      yPos += 5;
      const currentDateTime = new Date()
        .toLocaleString("en-US", {
          timeZone: "Asia/Karachi",
          day: "2-digit",
          month: "short",
          year: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
        .replace(/,/, "")
        .replace(/ /g, "-");
      doc.text(
        `Date: ${currentDateTime} PKT By: ${crossm.performedBy || "N/A"}`,
        margin,
        yPos
      );

      const pdfBlob = doc.output("blob");
      const blobUrl = URL.createObjectURL(pdfBlob);
      console.log("Current Print Data in handlePrintDesign:", crossm);
      setPdfContent(blobUrl);
      setCurrentPrintData(crossm);
      setPreviewModalOpen(true);
    } catch (error) {
      console.error("PDF Generation Error:", error);
      toast.error("Failed to generate PDF");
    }
  };

  const handlePrintFromPreview = () => {
    if (!currentPrintData) {
      toast.error("No data available to print");
      return;
    }

    try {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [80, 120],
      });

      const margin = 5;
      const pageWidth = 80;
      let yPos = 5;

      const pageHeader = currentPrintData.pageHeaderData || {};
      const hospitalTitle = pageHeader.title || projectProfile?.hospitalName;
      const address = pageHeader.address || projectProfile?.city;

      doc.setFont("times", "bold");
      doc.setFontSize(14);
      doc.text(hospitalTitle, pageWidth / 2, yPos, { align: "center" });
      yPos += 5;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(address, pageWidth / 2, yPos, { align: "center" });

      yPos += 2;
      doc.setLineWidth(0.5);
      doc.line(margin, yPos, pageWidth - margin, yPos);

      yPos += 2;
      const slipNo = currentPrintData.slipNo || "N/A";
      const slipNoCanvas = document.createElement("canvas");
      slipNoCanvas.width = 800;
      slipNoCanvas.height = 100;
      const ctx = slipNoCanvas.getContext("2d");
      if (ctx) {
        ctx.imageSmoothingEnabled = false;
      }
      jsbarcode(slipNoCanvas, slipNo, {
        format: "CODE128",
        width: 2.0,
        height: 17,
        displayValue: false,
        margin: 0,
      });
      const slipNoDataUrl = slipNoCanvas.toDataURL("image/png", 1.0);
      const barcodeWidth1 = 20;
      const barcodeX1 = margin;
      doc.addImage(slipNoDataUrl, "PNG", barcodeX1, yPos, barcodeWidth1, 10);
      yPos += 15;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Slip No: ${slipNo}`, margin, yPos);
      doc.text(
        `MR No: ${currentPrintData.patientDetails?.mrn || "N/A"}`,
        pageWidth - margin,
        yPos,
        { align: "right" }
      );
      yPos += 5;
         doc.text(
        `Ref MRN: ${currentPrintData.patientDetails?.ref_mrn || "-"}`,
        margin,
        yPos
      );
      yPos += 5;
      doc.text(
        `P. Name: ${currentPrintData.patientDetails?.patientName || "N/A"}`,
        margin,
        yPos
      );
      yPos += 5;
      doc.text(
        `Guardian Name: ${currentPrintData.patientDetails?.guardiansName || "N/A"
        }`,
        margin,
        yPos
      );
      yPos += 5;
      doc.text(
        `Gender: ${currentPrintData.patientDetails?.gender || "N/A"}`,
        margin,
        yPos
      );
      yPos += 5;
      console.log(
        "DOB in handlePrintFromPreview:",
        currentPrintData.patientDetails?.dob
      );
      doc.text(
        `Age: ${calculateAgeFromDOB(currentPrintData.patientDetails?.dob)}`,
        margin,
        yPos
      );
      yPos += 5;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      const bloodProduct =
        currentPrintData.bagComponent?.component ||
        (Array.isArray(currentPrintData.bagData?.bloodCell)
          ? currentPrintData.bagData.bloodCell.join(", ")
          : currentPrintData.bagData?.bloodCell) ||
        "N/A";
      const maxWidth = 50;
      const bloodProductText = `Product: ${bloodProduct}`;
      const truncatedText =
        doc.getTextWidth(bloodProductText) > maxWidth
          ? doc.splitTextToSize(bloodProductText, maxWidth)[0] + "..."
          : bloodProductText;
      doc.text(truncatedText, margin, yPos);
      yPos += 5;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(32);
      doc.text(
        `${currentPrintData.slipData?.bloodGroup || "N/A"}`,
        pageWidth - 10,
        yPos - 10,
        { align: "center" }
      );
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(
        `${currentPrintData.slipData?.rhFactor === "+" ? "POSITIVE" : "NEGATIVE"
        }`,
        pageWidth - 10,
        yPos - 5,
        { align: "center" }
      );

      yPos += 1;
      doc.setLineWidth(0.5);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 5;

      doc.setFontSize(10);
      doc.text(`Bag No: ${currentPrintData.bagNo || "N/A"}`, margin, yPos);
      yPos += 5;
      doc.text(
        `Collection Date: ${formatDate(currentPrintData.bagData?.collectionDate) || "N/A"
        }`,
        margin,
        yPos
      );
      yPos += 5;
      doc.text(
        `Expire Date: ${formatDate(currentPrintData.bagComponent?.expiryDate) || "N/A"
        }`,
        margin,
        yPos
      );
      yPos += 5;
      doc.setFont("helvetica", "bold");
      doc.text(truncatedText, margin, yPos);
      yPos += 1;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(32);
      doc.text(
        `${currentPrintData.bagData?.bloodGroup || "N/A"}`,
        pageWidth - 10,
        yPos - 10,
        { align: "center" }
      );
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(
        `${currentPrintData.bagData?.rhFactor === "+" ? "POSITIVE" : "NEGATIVE"
        }`,
        pageWidth - 10,
        yPos - 5,
        { align: "center" }
      );

      yPos += 1;
      const bagNo = currentPrintData.bagNo || "N/A";
      const bagNoCanvas = document.createElement("canvas");
      bagNoCanvas.width = 800;
      bagNoCanvas.height = 100;
      const ctx1 = bagNoCanvas.getContext("2d");
      if (ctx1) {
        ctx1.imageSmoothingEnabled = false;
      }
      jsbarcode(bagNoCanvas, bagNo, {
        format: "CODE128",
        width: 2.0,
        height: 17,
        displayValue: false,
        margin: 0,
      });
      const bagNoDataUrl = bagNoCanvas.toDataURL("image/png", 1.0);
      const barcodeWidth = 20;
      const barcodeX = margin;
      doc.addImage(bagNoDataUrl, "PNG", barcodeX, yPos, barcodeWidth, 10);
      yPos += 15;

      doc.setFontSize(6);
      const longText =
        // "This blood product has been screened for HBsAg, HCV, HIV, MP & VDRL. Store between 2 - 8°C.";
        `This blood product has been screened for HBsAg, HCV, HIV, MP & VDRL. ${currentPrintData.bagComponent.storageMessage}`;
      const wrappedText = doc.splitTextToSize(longText, pageWidth - 2 * margin);
      doc.text(wrappedText, margin, yPos);
      yPos += 5;
      const currentDateTime = new Date()
        .toLocaleString("en-US", {
          timeZone: "Asia/Karachi",
          day: "2-digit",
          month: "short",
          year: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
        .replace(/,/, "")
        .replace(/ /g, "-");
      doc.text(
        `Date: ${currentDateTime} PKT By: ${currentPrintData.performedBy || "N/A"
        }`,
        margin,
        yPos
      );

      const blobUrl = doc.output("bloburl");
      window.open(blobUrl, "_blank");
      
    } catch (error) {
      console.error("PDF Print Error:", error);
      toast.error("Failed to print PDF");
    }
  };

  // Modal close handler
  const handleClosePreviewModal = () => {
    if (pdfContent) URL.revokeObjectURL(pdfContent);
    setPreviewModalOpen(false);
    setPdfContent(undefined); // Changed from null to undefined
    setCurrentPrintData(null);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    const match = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return dayjs(`${match[1]}-${match[2]}-${match[3]}`).format("DD-MMM-YY");
    }
    return dayjs(dateString).format("DD-MMM-YY");
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    console.log("Rows per page input:", event.target.value);
    const value = Number(event.target.value);
    const validValues = [100, 150, 200];
    const newRowsPerPage = validValues.includes(value) ? value : 100;
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const headers = [
    "Slip No",
    "Bag No",
    "Donor Name",
    "Donor B.G",
    "Patient Name",
    "Pt B.G",
    "Result",
    "Quantity",
    "Issued At",
    "Actions",
  ];

  const tableData = crossMatch.map(item => {
    console.log(item, "Table Data");

    return {
      "Slip No": item.slipNo || "N/A",
      "Bag No": item.bagNo || "N/A",
      "Donor Name": item.donorData.name || "N/A",
      "Donor Blood Group": item.donorData?.bloodGroup
        ? item.donorData?.bloodGroup
        : "N/A",
      "Patient Name": item.patientDetails.patientName || "N/A",
      "Patient Blood Group": item.slipData.bloodGroup
        ? item.slipData.bloodGroup + item.slipData.rhFactor
        : "N/A",
      Result: item.crossMatchResult || "N/A",
      "Blood Quantity":item.bloodQty ? `${item.bloodQty} ml` : "--",

      "Issued At": item.createdAt
        ? dayjs(item.createdAt).format("DD-MMM-YY HH:mm")
        : "N/A",
    };
  });

  const actionButtons = [
    {
      icon: <EditIcon sx={{ color: "#FFB800", cursor: "pointer" }} />,
      onClick: handleEdit,
      disabled: () => false,
    },
    {
      icon: <PrintIcon sx={{ color: "green", cursor: "pointer" }} />,
      onClick: handlePrint,
      disabled: () => false,
    },
    {
      icon: (
        <img
          src={thermalPrinterIcon} // Use the imported image
          alt="Thermal Print"
          style={{ color: "blue", cursor: "pointer", width: 24, height: 24 }}
        />
      ),
      onClick: handlePrintDesign,
      disabled: () => false,
    },
  ];

  return (
    <Box>
      <Box sx={{ background: "#fff", p: 1, borderRadius: "8px", mb: 0.5 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
        >
          <Typography sx={{ fontSize: "16px", fontWeight: "bold" }}>
            Cross Match Reports
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={() => navigate("/add-donor")}
            sx={{ fontSize: { md: "14px", sm: "12px", xs: "10px" } }}
          >
            Add Donor
          </Button>
        </Box>
        <Box
          display="flex"
          alignItems="flex-end"
          flexWrap="wrap"
          gap={1.5}
          mt={1}
        >
          <TextField
            className="custom-input"
            size="small"
            label="Search"
            placeholder="Slip, bag, donor or patient"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            sx={{ width: { xs: "100%", sm: 220 } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            className="custom-input"
            select
            size="small"
            label="Result"
            value={resultFilter}
            onChange={e => {
              setResultFilter(e.target.value);
              setPage(0);
            }}
            sx={{ width: { xs: "100%", sm: 150 } }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Compatible">Compatible</MenuItem>
            <MenuItem value="Incompatible">Incompatible</MenuItem>
            <MenuItem value="Pending">Pending</MenuItem>
          </TextField>
          <TextField
            className="custom-input"
            select
            size="small"
            label="Patient B.G"
            value={bloodGroup}
            onChange={e => {
              setBloodGroup(e.target.value);
              setPage(0);
            }}
            sx={{ width: { xs: "100%", sm: 130 } }}
          >
            <MenuItem value="">All</MenuItem>
            {BLOOD_GROUPS.map(group => (
              <MenuItem key={group} value={group}>
                {group}
              </MenuItem>
            ))}
          </TextField>
          <DateRangeSelector
            preset="blood"
            compact
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={value => {
              setDateRange(value);
              setPage(0);
            }}
            onFromDateChange={value => {
              setFromDate(value);
              setPage(0);
            }}
            onToDateChange={value => {
              setToDate(value);
              setPage(0);
            }}
          />
        </Box>
      </Box>
      <Paper sx={{ p: 1, borderRadius: "8px" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <DynamicTable
              headers={headers}
              data={tableData}
              loading={loading}
              page={page}
              rowsPerPage={rowsPerPage}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              totalRows={total}
              actionButtons={actionButtons}
            />
          </>
        )}
      </Paper>

      <Modal
        open={previewModalOpen}
        onClose={handleClosePreviewModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            padding: "20px",
            borderRadius: "5px",
            width: "300px",
          }}
        >
          <Typography id="modal-modal-title" variant="h6" component="h2">
            PDF Preview
          </Typography>
          {pdfContent ? (
            <embed src={pdfContent} width="100%" height="400px" />
          ) : (
            <Typography color="error">Failed to load PDF</Typography>
          )}
          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              onClick={handlePrintFromPreview}
              variant="contained"
              color="primary"
              sx={{ mr: 1 }}
              disabled={!currentPrintData}
            >
              Print
            </Button>
            <Button
              onClick={handleClosePreviewModal}
              variant="contained"
              color="secondary"
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default CrossMatchReport;
